import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HeroKpiCard,
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { digitalTwinService } from '@/services/digitalTwin.service';

const DigitalTwinPage: React.FC = () => {
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [compareResult, setCompareResult] = useState<Record<string, unknown> | null>(null);

  const dashQ = useQuery({
    queryKey: ['edt-dashboard'],
    queryFn: () => digitalTwinService.getDashboard(),
    staleTime: 60_000,
  });
  const catalogQ = useQuery({
    queryKey: ['edt-catalog'],
    queryFn: () => digitalTwinService.getCatalog(),
    staleTime: 300_000,
  });
  const simsQ = useQuery({
    queryKey: ['edt-simulations'],
    queryFn: () => digitalTwinService.listSimulations(),
    staleTime: 30_000,
  });
  const histQ = useQuery({
    queryKey: ['edt-history'],
    queryFn: () => digitalTwinService.getHistory(),
    staleTime: 60_000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['edt-dashboard'] });
    void qc.invalidateQueries({ queryKey: ['edt-simulations'] });
    void qc.invalidateQueries({ queryKey: ['edt-history'] });
  };

  const snapMut = useMutation({
    mutationFn: () => digitalTwinService.createSnapshot('Executive workspace snapshot'),
    onSuccess: () => {
      message.success('Immutable snapshot created');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const runMut = useMutation({
    mutationFn: digitalTwinService.runSimulation,
    onSuccess: () => {
      message.success('Simulation completed (advisory only)');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const compareMut = useMutation({
    mutationFn: () => digitalTwinService.compare(selectedIds, 'Executive compare'),
    onSuccess: (data) => {
      setCompareResult(data);
      message.success('Comparison ready');
    },
    onError: (e: Error) => message.error(e.message),
  });

  const promoteMut = useMutation({
    mutationFn: (id: string) => digitalTwinService.promote(id, true),
    onSuccess: () => message.success('Promoted to EOAF action (explicit confirm)'),
    onError: (e: Error) => message.error(e.message),
  });

  const forecastMut = useMutation({
    mutationFn: (horizonDays: number) => digitalTwinService.forecast({ horizonDays }),
    onSuccess: () => message.success('Forecast generated'),
    onError: (e: Error) => message.error(e.message),
  });

  const catalog = catalogQ.data || [];
  const sims = (simsQ.data || []) as Array<Record<string, unknown>>;
  const dash = dashQ.data;

  const compareRows = useMemo(() => {
    const rows = (compareResult?.rows as Array<Record<string, unknown>>) || [];
    return rows;
  }, [compareResult]);

  return (
    <OiPageFrame
      subtitle="Simulate decisions on immutable ERP snapshots — never changes live data. What will happen if we decide this?"
      loading={dashQ.isLoading && !dash}
      error={Boolean(dashQ.error)}
      errorMessage="Digital Twin unavailable"
    >
      {dash?.degrade && (dash.degrade as any)?.jobs_disabled ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          EDT jobs disabled (EDT_JOBS_DISABLED=1). Snapshots/simulations still available on demand.
        </Card>
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <HeroKpiCard label="Active / recent sims" value={String((dash as any)?.activeCount ?? '—')} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Completed" value={(dash as any)?.completedCount ?? '—'} />
        </Col>
        <Col xs={12} md={4}>
          <PrimaryKpiCard label="Snapshots" value={(dash as any)?.snapshotCount ?? '—'} />
        </Col>
        <Col xs={12} md={5}>
          <PrimaryKpiCard label="Catalog scenarios" value={catalog.length} />
        </Col>
        <Col xs={12} md={5}>
          <Space>
            <Button onClick={() => snapMut.mutate()} loading={snapMut.isPending}>
              Take snapshot
            </Button>
            <Button onClick={() => forecastMut.mutate(90)}>90d forecast</Button>
          </Space>
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'run',
            label: 'Run scenario',
            children: (
              <Card bordered={false} title="What-if runner">
                <Form
                  layout="vertical"
                  onFinish={(v) =>
                    runMut.mutate({
                      scenarioType: v.scenarioType,
                      title: v.title,
                      params: {
                        days: v.days,
                        amount: v.amount,
                        pct: v.pct,
                        headcount: v.headcount,
                      },
                    })
                  }
                >
                  <Form.Item name="scenarioType" label="Scenario" rules={[{ required: true }]}>
                    <Select
                      showSearch
                      options={catalog.map((c) => ({
                        value: String(c.code),
                        label: `${c.domain}: ${c.title}`,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name="title" label="Title">
                    <Input placeholder="Optional title" />
                  </Form.Item>
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item name="days" label="Days">
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="amount" label="Amount">
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="pct" label="Pct">
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button type="primary" htmlType="submit" loading={runMut.isPending}>
                    Run simulation
                  </Button>
                </Form>
                {runMut.data ? (
                  <pre style={{ marginTop: 16, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify((runMut.data as any).result || runMut.data, null, 2)}
                  </pre>
                ) : null}
              </Card>
            ),
          },
          {
            key: 'sims',
            label: 'Simulations',
            children: (
              <Card bordered={false} title="Simulation history">
                {!sims.length ? (
                  <OiEmptyState title="No simulations yet" body="Run a scenario to populate history." />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={sims}
                    rowSelection={{
                      selectedRowKeys: selectedIds,
                      onChange: (keys) => setSelectedIds(keys as string[]),
                    }}
                    columns={[
                      { title: 'Title', dataIndex: 'title' },
                      { title: 'Type', dataIndex: 'scenario_type', width: 160 },
                      { title: 'Domain', dataIndex: 'domain', width: 110 },
                      { title: 'Status', dataIndex: 'status', width: 100 },
                      { title: 'Conf.', dataIndex: 'confidence', width: 70 },
                      {
                        title: 'Promote',
                        width: 110,
                        render: (_: unknown, row: Record<string, unknown>) =>
                          String(row.status) === 'completed' ? (
                            <Button
                              size="small"
                              onClick={() => promoteMut.mutate(String(row.id))}
                            >
                              To EOAF
                            </Button>
                          ) : (
                            <Tag>—</Tag>
                          ),
                      },
                    ]}
                  />
                )}
                <Button
                  style={{ marginTop: 12 }}
                  disabled={selectedIds.length < 2}
                  loading={compareMut.isPending}
                  onClick={() => compareMut.mutate()}
                >
                  Compare selected ({selectedIds.length})
                </Button>
              </Card>
            ),
          },
          {
            key: 'compare',
            label: 'Compare',
            children: (
              <Card bordered={false} title="Side-by-side comparison">
                {!compareRows.length ? (
                  <OiEmptyState
                    title="No comparison yet"
                    body="Select 2+ completed simulations and click Compare."
                  />
                ) : (
                  <>
                    <div style={{ marginBottom: 8 }}>{String(compareResult?.explanation || '')}</div>
                    <Table
                      size="small"
                      rowKey="simulationId"
                      dataSource={compareRows}
                      columns={[
                        { title: 'Title', dataIndex: 'title' },
                        { title: 'Score', dataIndex: 'score', width: 80 },
                        { title: 'Cash Δ', dataIndex: 'cashDelta', width: 100 },
                        { title: 'Risk', dataIndex: 'riskLevel', width: 90 },
                        { title: 'Delivery', dataIndex: 'deliveryRisk', width: 100 },
                        { title: 'Slip', dataIndex: 'projectSlipDays', width: 70 },
                        { title: 'Conf.', dataIndex: 'confidence', width: 70 },
                      ]}
                    />
                  </>
                )}
              </Card>
            ),
          },
          {
            key: 'history',
            label: 'Audit history',
            children: (
              <Card bordered={false}>
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={(histQ.data as Array<Record<string, unknown>>) || []}
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Status', dataIndex: 'status', width: 100 },
                    { title: 'Type', dataIndex: 'scenario_type', width: 160 },
                    {
                      title: 'Created',
                      dataIndex: 'created_at',
                      width: 160,
                      render: (v: string) => (v ? String(v).slice(0, 19) : '—'),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
    </OiPageFrame>
  );
};

export default DigitalTwinPage;
